import { useState } from 'react';
import type { ReactNode } from 'react';
import type { Invoice, InvoiceDetail } from '@faktorplus/shared';
import { InvoiceDocument } from './invoice-document';
import { AttachmentPreview } from './attachment-preview';
import styles from './invoice-document.module.css';

type Tab = 'data' | 'scan';

interface InvoiceViewProps {
  invoice: Invoice | InvoiceDetail;
  /**
   * File management for the scan, for viewers allowed to change it. It belongs
   * under the preview rather than in the sidebar: "wgraj plik" next to a panel
   * that already says "Brak skanu" needs no explaining.
   */
  upload?: ReactNode;
}

/**
 * The invoice, with the scan demoted to a second tab.
 *
 * The data view is the default because it always exists — roughly two thirds
 * of invoices arrive from KSeF with no PDF, and a screen whose primary panel
 * says "Brak skanu" would look broken for all of them.
 */
export function InvoiceView({ invoice, upload }: InvoiceViewProps) {
  const [tab, setTab] = useState<Tab>('data');
  const scanCount = invoice.attachments.length;

  // With no scan there is nothing to look at — unless this viewer can add one,
  // in which case the tab is the way to reach the upload.
  const scanDisabled = scanCount === 0 && !upload;

  return (
    <div>
      <div className={styles.tabs} role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'data'}
          className={`${styles.tab} ${tab === 'data' ? styles.tabActive : ''}`}
          onClick={() => setTab('data')}
        >
          Dane faktury
        </button>

        <button
          type="button"
          role="tab"
          aria-selected={tab === 'scan'}
          className={`${styles.tab} ${tab === 'scan' ? styles.tabActive : ''}`}
          onClick={() => setTab('scan')}
          disabled={scanDisabled}
          title={scanDisabled ? 'Ta faktura nie ma skanu' : undefined}
        >
          Skan
          {scanCount > 0 && <span className={styles.tabCount}>{scanCount}</span>}
        </button>
      </div>

      {tab === 'data' ? (
        <InvoiceDocument invoice={invoice} />
      ) : (
        <div className={styles.scanPanel}>
          <AttachmentPreview attachment={invoice.attachments[0]} />
          {upload}
        </div>
      )}
    </div>
  );
}
